@file:OptIn(kotlin.time.ExperimentalTime::class)

package com.yourssu.lms_api_guide

import io.github.chlwhdtn03.*
import io.github.chlwhdtn03.data.Lms.Semester
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import kotlinx.coroutines.suspendCancellableCoroutine
import kotlinx.coroutines.withTimeout
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import org.w3c.dom.*
import kotlin.coroutines.resume

private const val DASHBOARD_REQUEST_TIMEOUT_MS = 45_000L
private const val DEVELOPER_REQUEST_TIMEOUT_MS = 20_000L

private val prettyJson = Json {
    prettyPrint = true
    ignoreUnknownKeys = true
}

private val dashboardLoadOrder = listOf(
    ApiEndpoint.SEMESTER_GRADE_SUMMARY,
    ApiEndpoint.TIMETABLE,
    ApiEndpoint.CHAPEL,
    ApiEndpoint.GRADES,
    ApiEndpoint.GRADUATE,
    ApiEndpoint.TUITION,
    ApiEndpoint.SCHOLARSHIP,
    ApiEndpoint.TERMS,
)

private data class CachedApiResponse(
    val json: String? = null,
    val error: String? = null,
)

private data class DashboardGradePoint(
    val sortKey: Int,
    val label: String,
    val gpaText: String,
    val gpa: Double,
)

private data class DashboardLoadError(
    val endpoint: ApiEndpoint,
    val message: String,
)

enum class ApiEndpoint(
    val functionName: String,
    val title: String,
    val description: String,
    val hasSemesterParameters: Boolean = false,
) {
    TIMETABLE(
        functionName = "LmsApi.getTimetable()",
        title = "시간표 조회",
        description = "선택한 학년도와 학기의 강의 시간표를 조회합니다.",
        hasSemesterParameters = true,
    ),
    GRADES(
        functionName = "LmsApi.getGradeTable()",
        title = "성적표 조회",
        description = "선택한 학기의 과목별 학점과 성적을 조회합니다.",
        hasSemesterParameters = true,
    ),
    SEMESTER_GRADE_SUMMARY(
        functionName = "LmsApi.getSemesterGradeSummaryTable()",
        title = "학기별 성적 요약",
        description = "전체 학기의 신청·취득 학점, 평점, 석차와 학사 상태를 조회합니다.",
    ),
    SCHOLARSHIP(
        functionName = "LmsApi.getScholarshipHistoryTable()",
        title = "장학금 이력 조회",
        description = "학기별 장학금 선발 및 실제 수혜 내역을 조회합니다.",
    ),
    TUITION(
        functionName = "LmsApi.getTuitionTable()",
        title = "등록금 조회",
        description = "등록금 고지 금액과 감면 및 납부 내역을 조회합니다.",
    ),
    GRADUATE(
        functionName = "LmsApi.getGraduateTable()",
        title = "졸업 사정표 조회",
        description = "영역별 졸업 기준과 현재 이수 현황을 조회합니다.",
    ),
    CHAPEL(
        functionName = "LmsApi.getChapelTable()",
        title = "채플 정보 조회",
        description = "선택한 학기의 채플 좌석과 출결 정보를 조회합니다.",
        hasSemesterParameters = true,
    ),
    TERMS(
        functionName = "LmsApi.getTerms()",
        title = "LMS 학기 목록",
        description = "LMS에서 사용할 수 있는 전체 학기 목록을 조회합니다.",
    ),
}

class BootstrapDashboard {
    private val scope = MainScope()
    private var selectedEndpoint = ApiEndpoint.SEMESTER_GRADE_SUMMARY
    private var selectedDeveloperEndpoint = ApiEndpoint.TIMETABLE
    private var selectedDeveloperLanguage = "kotlin"
    private var selectedDeveloperUsage = "suspend"
    private var loggedIn = false
    private var dashboardLoading = false
    private var showLookupProgress = true
    private var loadingEndpoint: ApiEndpoint? = null
    private var lastDeveloperJson = ""
    private var lastErrorLog = ""
    private var loadAttempt = 0
    private val dashboardResponses = mutableMapOf<ApiEndpoint, CachedApiResponse>()
    private val developerResponseCache = mutableMapOf<String, String>()

    fun start() {
        bindEndpointButtons()
        bindLoginForm()
        bindRefreshButton()
        bindErrorLogCopyButton()
        bindDeveloperConsole()
        bindDeveloperCodeControls()
        selectEndpoint(selectedEndpoint)
        selectDeveloperEndpoint(selectedDeveloperEndpoint)
        setLoggedIn(false)
        document.body?.classList?.remove("is-loading")
    }

    private fun bindEndpointButtons() {
        document.querySelectorAll("[data-endpoint]").forEachElement { element ->
            element.addEventListener("click", {
                val name = element.getAttribute("data-endpoint") ?: return@addEventListener
                val endpoint = ApiEndpoint.valueOf(name)
                selectEndpoint(endpoint)
                element<HTMLElement>(if (endpoint == ApiEndpoint.TIMETABLE) "schedule-preview" else "lookup").scrollIntoView()
            })
        }
    }

    private fun bindLoginForm() {
        element<HTMLFormElement>("login-form").addEventListener("submit", { event ->
            event.preventDefault()
            val studentIdInput = element<HTMLInputElement>("student-id")
            val id = studentIdInput.value.trim()
            val passwordInput = element<HTMLInputElement>("password")
            val password = passwordInput.value

            if (id.isBlank() || password.isBlank()) {
                showLoginError("학번과 비밀번호를 모두 입력해주세요.")
                return@addEventListener
            }

            setLoginLoading(true)
            showLoginError(null)
            LmsApi.loginLMS(id, password) { result ->
                setLoginLoading(false)
                if (result.success) {
                    setLoggedIn(true)
                    hideLoginModal()
                    loadDashboardData()
                } else {
                    showLoginError(result.errorMessage ?: "로그인에 실패했습니다. 계정 정보를 확인해주세요.")
                }
            }

            // The password is removed immediately. The student ID remains in the
            // form after a failed attempt, but is cleared when the modal closes.
            passwordInput.value = ""
        })

        element<HTMLElement>("logout-button").addEventListener("click", {
            window.location.assign("/session/logout")
        })

        element<HTMLElement>("login-modal").addEventListener("hidden.bs.modal", {
            element<HTMLFormElement>("login-form").reset()
            showLoginError(null)
        })
    }

    private fun bindRefreshButton() {
        element<HTMLButtonElement>("refresh-button").addEventListener("click", {
            if (!loggedIn) {
                showLoginModal()
                return@addEventListener
            }
            loadDashboardData()
        })
    }

    private fun bindErrorLogCopyButton() {
        element<HTMLButtonElement>("error-log-copy").addEventListener("click", {
            if (lastErrorLog.isBlank()) return@addEventListener
            window.navigator.clipboard.writeText(lastErrorLog)
            val button = element<HTMLButtonElement>("error-log-copy")
            val original = button.innerHTML
            button.innerHTML = "<i class=\"bi bi-check-lg\"></i><span>복사됨</span>"
            window.setTimeout({ button.innerHTML = original }, 1400)
        })
    }

    private fun bindDeveloperConsole() {
        document.querySelectorAll("[data-dev-endpoint]").forEachElement { item ->
            item.addEventListener("click", {
                val name = item.getAttribute("data-dev-endpoint") ?: return@addEventListener
                selectDeveloperEndpoint(ApiEndpoint.valueOf(name))
            })
        }

        element<HTMLButtonElement>("dev-run-button").addEventListener("click", {
            if (!loggedIn) {
                showLoginModal()
                return@addEventListener
            }

            val endpoint = selectedDeveloperEndpoint
            val year = element<HTMLInputElement>("dev-year-input").value
            val semester = element<HTMLSelectElement>("dev-semester-select").value.toSemester()
            setDeveloperLoading(true)
            scope.launch {
                var responseJson: String? = null
                var errorMessage: String? = null
                try {
                    executeApi(
                        endpoint = endpoint,
                        year = year,
                        semester = semester,
                        timeoutMs = DEVELOPER_REQUEST_TIMEOUT_MS,
                        onSuccess = { responseJson = it },
                        onFailure = { errorMessage = it },
                    )
                } finally {
                    setDeveloperLoading(false)
                }

                val json = responseJson
                if (json != null) {
                    val (resolvedJson, usedLastFullResponse) = resolveDeveloperJson(endpoint, year, semester, json)
                    lastDeveloperJson = resolvedJson
                    renderDeveloperResponse(
                        content = resolvedJson,
                        success = true,
                        statusText = if (usedLastFullResponse) "200 OK · LAST FULL RESPONSE" else null,
                    )
                } else {
                    lastDeveloperJson = ""
                    renderDeveloperResponse(errorMessage ?: "요청 결과를 확인하지 못했습니다.", success = false)
                }
            }
        })

        element<HTMLButtonElement>("dev-copy-button").addEventListener("click", {
            if (lastDeveloperJson.isBlank()) return@addEventListener
            window.navigator.clipboard.writeText(lastDeveloperJson)
            val button = element<HTMLButtonElement>("dev-copy-button")
            val original = button.innerHTML
            button.innerHTML = "<i class=\"bi bi-check-lg\"></i><span>복사됨</span>"
            window.setTimeout({ button.innerHTML = original }, 1400)
        })
    }

    private fun bindDeveloperCodeControls() {
        document.querySelectorAll("[data-dev-usage]").forEachElement { item ->
            item.addEventListener("click", {
                if ((item as? HTMLButtonElement)?.disabled == true) return@addEventListener
                selectedDeveloperUsage = item.getAttribute("data-dev-usage") ?: "suspend"
                renderDeveloperUsageExample()
            })
        }
        document.querySelectorAll("[data-dev-language]").forEachElement { item ->
            item.addEventListener("click", {
                selectedDeveloperLanguage = item.getAttribute("data-dev-language") ?: "kotlin"
                renderDeveloperUsageExample()
            })
        }
        element<HTMLInputElement>("dev-year-input").addEventListener("input", { renderDeveloperUsageExample() })
        element<HTMLSelectElement>("dev-semester-select").addEventListener("change", { renderDeveloperUsageExample() })
        element<HTMLButtonElement>("dev-code-copy").addEventListener("click", {
            val snippet = element<HTMLElement>("dev-usage-snippet").textContent.orEmpty()
            if (snippet.isBlank()) return@addEventListener
            window.navigator.clipboard.writeText(snippet)
            val button = element<HTMLButtonElement>("dev-code-copy")
            val original = button.innerHTML
            button.innerHTML = "<i class=\"bi bi-check-lg\"></i><span>복사됨</span>"
            window.setTimeout({ button.innerHTML = original }, 1400)
        })
    }

    private fun selectDeveloperEndpoint(endpoint: ApiEndpoint) {
        selectedDeveloperEndpoint = endpoint
        document.querySelectorAll("[data-dev-endpoint]").forEachElement { item ->
            item.classList.toggle("active", item.getAttribute("data-dev-endpoint") == endpoint.name)
        }
        element<HTMLElement>("dev-endpoint-function").textContent = endpoint.functionName
        element<HTMLElement>("dev-endpoint-title").textContent = endpoint.title
        element<HTMLElement>("dev-endpoint-description").textContent = endpoint.description
        element<HTMLElement>("dev-parameter-panel").classList.toggle("d-none", !endpoint.hasSemesterParameters)
        if (!endpoint.supportsSuspend && selectedDeveloperUsage == "suspend") {
            selectedDeveloperUsage = "callback"
        }
        renderDeveloperUsageExample()

        // The dashboard has already loaded these endpoints after login. Keep the
        // developer console useful by showing that original response immediately;
        // the run button remains available when a fresh request is needed.
        val cachedResponse = dashboardResponses[endpoint]
        when {
            cachedResponse?.json != null -> {
                lastDeveloperJson = cachedResponse.json
                renderDeveloperResponse(cachedResponse.json, success = true)
            }

            cachedResponse?.error != null -> {
                lastDeveloperJson = ""
                renderDeveloperResponse(cachedResponse.error, success = false)
            }

            else -> clearDeveloperResponse()
        }
    }

    private fun renderDeveloperUsageExample() {
        val suspendButton = document.querySelector("[data-dev-usage=\"suspend\"]") as? HTMLButtonElement
        suspendButton?.disabled = !selectedDeveloperEndpoint.supportsSuspend

        document.querySelectorAll("[data-dev-usage]").forEachElement { item ->
            val selected = item.getAttribute("data-dev-usage") == selectedDeveloperUsage
            item.classList.toggle("active", selected)
            item.setAttribute("aria-pressed", selected.toString())
        }
        document.querySelectorAll("[data-dev-language]").forEachElement { item ->
            val selected = item.getAttribute("data-dev-language") == selectedDeveloperLanguage
            item.classList.toggle("active", selected)
            item.setAttribute("aria-pressed", selected.toString())
        }

        val note = element<HTMLElement>("dev-usage-note")
        val callbackOnly = !selectedDeveloperEndpoint.supportsSuspend
        note.classList.toggle("d-none", !callbackOnly)
        note.textContent = if (callbackOnly) {
            "getTerms()의 suspend 함수는 라이브러리 내부 API입니다. 공개 API에서는 Callback을 사용합니다."
        } else {
            ""
        }

        val year = element<HTMLInputElement>("dev-year-input").value.ifBlank { "2026" }
        val semester = element<HTMLSelectElement>("dev-semester-select").value
        element<HTMLElement>("dev-usage-snippet").textContent = selectedDeveloperEndpoint.usageSnippet(
            language = selectedDeveloperLanguage,
            usage = selectedDeveloperUsage,
            year = year,
            semester = semester,
        )
    }

    private fun selectEndpoint(endpoint: ApiEndpoint) {
        selectedEndpoint = endpoint
        document.querySelectorAll("[data-endpoint]").forEachElement { item ->
            val selected = item.getAttribute("data-endpoint") == endpoint.name
            item.classList.toggle("active", selected)
            item.setAttribute("aria-current", if (selected) "true" else "false")
        }

        val financialRecord = endpoint == ApiEndpoint.TUITION || endpoint == ApiEndpoint.SCHOLARSHIP
        element<HTMLElement>("endpoint-title").textContent = if (financialRecord) "등록금·장학금 조회" else endpoint.title
        element<HTMLElement>("endpoint-description").textContent = if (financialRecord) {
            "등록금 납부와 장학금 수혜 내역을 한곳에서 확인합니다."
        } else {
            endpoint.description
        }
        element<HTMLElement>("parameter-panel").classList.add("d-none")
        if (loggedIn) renderDashboardResponse(endpoint) else clearResponse()
    }

    private fun setLoggedIn(value: Boolean) {
        loggedIn = value
        element<HTMLElement>("guest-actions").classList.toggle("d-none", value)
        element<HTMLElement>("user-actions").classList.toggle("d-none", !value)
        element<HTMLElement>("login-gate").classList.toggle("d-none", value)
        element<HTMLButtonElement>("refresh-button").classList.toggle("d-none", !value)
        element<HTMLElement>("student-label").textContent = "안전하게 연결됨"

        updateRefreshButton()
        updateDeveloperRunButton()
    }

    private fun updateRefreshButton() {
        val button = element<HTMLButtonElement>("refresh-button")
        button.disabled = dashboardLoading
        button.innerHTML = if (dashboardLoading) {
            "<span class=\"spinner-border spinner-border-sm\" aria-hidden=\"true\"></span><span>새로고침 중</span>"
        } else {
            "<i class=\"bi bi-arrow-clockwise\" aria-hidden=\"true\"></i><span>새로고침</span>"
        }
    }

    private fun updateDeveloperRunButton() {
        val runButton = element<HTMLButtonElement>("dev-run-button")
        runButton.innerHTML = if (loggedIn) {
            "<i class=\"bi bi-play-fill\"></i><span>요청 실행</span>"
        } else {
            "<i class=\"bi bi-lock-fill\"></i><span>연결 후 요청</span>"
        }
    }

    private fun setLoginLoading(value: Boolean) {
        val button = element<HTMLButtonElement>("login-submit")
        button.disabled = value
        button.innerHTML = if (value) {
            "<span class=\"spinner-border spinner-border-sm\" aria-hidden=\"true\"></span><span>인증 중</span>"
        } else {
            "<i class=\"bi bi-box-arrow-in-right\"></i><span>로그인</span>"
        }
    }

    private fun loadDashboardData() {
        if (dashboardLoading) return

        val year = element<HTMLInputElement>("year-input").value
        val semester = element<HTMLSelectElement>("semester-select").value.toSemester()
        val previousResponses = dashboardResponses.toMap()
        val isRefresh = previousResponses.isNotEmpty()
        showLookupProgress = !isRefresh
        loadAttempt += 1
        dashboardLoading = true
        updateRefreshButton()
        showDashboardLoadState(isRefresh)
        if (showLookupProgress) {
            showLookupLoading("전체 학사 정보를 준비하고 있어요", "로그인이 완료되어 첫 번째 정보를 요청합니다.")
        }

        scope.launch {
            val errors = mutableListOf<DashboardLoadError>()
            dashboardLoadOrder.forEachIndexed { index, endpoint ->
                loadingEndpoint = endpoint
                updateDashboardLoadProgress(index, endpoint)
                var response: CachedApiResponse? = null
                executeApi(
                    endpoint = endpoint,
                    year = year,
                    semester = semester,
                    onSuccess = { json -> response = CachedApiResponse(json = json) },
                    onFailure = { message -> response = CachedApiResponse(error = sanitizeDiagnosticMessage(message)) },
                )
                val completedResponse = response ?: CachedApiResponse(error = "응답을 확인하지 못했습니다.")
                if (completedResponse.error != null) {
                    errors += DashboardLoadError(endpoint, completedResponse.error)
                    dashboardResponses[endpoint] = previousResponses[endpoint]?.takeIf { it.json != null } ?: completedResponse
                } else {
                    dashboardResponses[endpoint] = completedResponse
                    completedResponse.json?.let { updateDashboardSummary(endpoint, it) }
                }
                updateDashboardLoadProgress(index + 1, endpoint)
            }

            loadingEndpoint = null
            dashboardLoading = false
            updateRefreshButton()
            finishDashboardLoadState(errors.size)
            renderDashboardResponse(selectedEndpoint)
            if (errors.isNotEmpty()) showLoadErrorModal(errors, isRefresh)
        }
    }

    private fun showDashboardLoadState(isRefresh: Boolean) {
        val state = element<HTMLElement>("dashboard-load-state")
        state.classList.remove("d-none", "is-complete")
        element<HTMLElement>("dashboard-load-icon").innerHTML = "<span class=\"dashboard-spinner\"></span>"
        element<HTMLElement>("dashboard-load-kicker").textContent = if (isRefresh) "학사 정보 새로고침" else "학사 정보 불러오는 중"
        element<HTMLElement>("dashboard-load-title").textContent = if (isRefresh) "최신 정보를 확인하고 있어요" else "로그인이 완료됐어요"
        element<HTMLElement>("dashboard-load-detail").textContent = if (isRefresh) {
            "기존 화면은 유지한 채 정보만 새로 불러옵니다."
        } else {
            "필요한 정보를 차례로 준비하고 있습니다."
        }
        element<HTMLElement>("dashboard-load-count").textContent = "0 / ${dashboardLoadOrder.size}"
        element<HTMLElement>("dashboard-load-bar").style.width = "0%"
    }

    private fun updateDashboardLoadProgress(completedCount: Int, endpoint: ApiEndpoint) {
        val total = dashboardLoadOrder.size
        val safeCount = completedCount.coerceIn(0, total)
        val percentage = safeCount * 100 / total
        element<HTMLElement>("dashboard-load-title").textContent = if (safeCount == total) {
            "마지막 정보를 정리하고 있어요"
        } else {
            "${endpoint.title} 불러오는 중"
        }
        element<HTMLElement>("dashboard-load-detail").textContent = "완료된 정보는 대시보드에 바로 보관됩니다."
        element<HTMLElement>("dashboard-load-count").textContent = "$safeCount / $total"
        element<HTMLElement>("dashboard-load-bar").style.width = "$percentage%"
        if (safeCount < total && showLookupProgress) {
            showLookupLoading("전체 학사 정보를 불러오는 중", "현재 ${endpoint.title}을(를) 확인하고 있어요.")
        }
    }

    private fun finishDashboardLoadState(failures: Int) {
        val state = element<HTMLElement>("dashboard-load-state")
        state.classList.add("is-complete")
        element<HTMLElement>("dashboard-load-icon").innerHTML = "<i class=\"bi bi-check-lg\" aria-hidden=\"true\"></i>"
        element<HTMLElement>("dashboard-load-kicker").textContent = "불러오기 완료"
        element<HTMLElement>("dashboard-load-title").textContent = if (failures == 0) {
            "학사 정보를 모두 준비했어요"
        } else {
            "${dashboardLoadOrder.size - failures}개 정보를 준비했어요"
        }
        element<HTMLElement>("dashboard-load-detail").textContent = if (failures == 0) {
            "이제 메뉴를 이동해도 다시 요청하지 않고 바로 보여드려요."
        } else {
            "확인하지 못한 ${failures}개 항목은 해당 메뉴에서 안내해 드려요."
        }
        element<HTMLElement>("dashboard-load-count").textContent = "${dashboardLoadOrder.size} / ${dashboardLoadOrder.size}"
        element<HTMLElement>("dashboard-load-bar").style.width = "100%"
        window.setTimeout({
            if (!dashboardLoading) state.classList.add("d-none")
        }, if (failures == 0) 2200 else 4000)
    }

    private fun showLoadErrorModal(errors: List<DashboardLoadError>, isRefresh: Boolean) {
        val timestamp = currentDiagnosticTimestamp()
        lastErrorLog = buildString {
            appendLine("LMS Dashboard Load Diagnostics")
            appendLine("Timestamp: $timestamp")
            appendLine("Operation: ${if (isRefresh) "manual-refresh" else "initial-dashboard-load"}")
            appendLine("Attempt: $loadAttempt")
            appendLine("Failed: ${errors.size}/${dashboardLoadOrder.size}")
            appendLine("Credentials: not-collected")
            errors.forEachIndexed { index, error ->
                appendLine()
                appendLine("[${index + 1}] ${error.endpoint.name}")
                appendLine("Function: ${error.endpoint.functionName}")
                appendLine("Message: ${sanitizeDiagnosticMessage(error.message)}")
                appendLine("Cached fallback: ${dashboardResponses[error.endpoint]?.json != null}")
            }
        }.trim()
        element<HTMLElement>("error-log-summary").textContent = "${errors.size}개 항목을 불러오지 못했습니다. 나머지 정보는 정상적으로 유지됩니다."
        element<HTMLElement>("error-log-content").textContent = lastErrorLog
        window.asDynamic().bootstrap.Modal.getOrCreateInstance(element<HTMLElement>("error-log-modal")).show()
    }

    private fun showLookupLoading(title: String, detail: String) {
        element<HTMLElement>("response-loading-title").textContent = title
        element<HTMLElement>("response-loading-detail").textContent = detail
        element<HTMLElement>("response-empty").classList.add("d-none")
        element<HTMLElement>("response-panel").classList.add("d-none")
        element<HTMLElement>("response-loading").classList.remove("d-none")
    }

    private fun renderDashboardResponse(endpoint: ApiEndpoint) {
        val response = dashboardResponses[endpoint]
        if (response == null) {
            if (dashboardLoading) {
                val current = loadingEndpoint?.title ?: "학사 정보"
                showLookupLoading("전체 학사 정보를 불러오는 중", "현재 ${current}을(를) 확인하고 있어요.")
            } else {
                clearResponse()
            }
            return
        }

        response.json?.let { json ->
            setResponseStatus(success = true)
            renderPreview(endpoint, json)
            showResponsePanel()
            return
        }

        renderApiError(response.error ?: "정보를 불러오지 못했습니다.")
        showResponsePanel()
    }

    private fun updateDashboardSummary(endpoint: ApiEndpoint, json: String) {
        try {
            when (endpoint) {
                ApiEndpoint.SEMESTER_GRADE_SUMMARY -> updateGradeSummary(json)
                ApiEndpoint.TIMETABLE -> updateTimetableSummary(json)
                ApiEndpoint.CHAPEL -> updateChapelSummary(json)
                else -> Unit
            }
        } catch (_: Throwable) {
            // The detailed cached response is still available even when an
            // optional dashboard summary cannot be derived from it.
        }
    }

    private fun updateGradeSummary(json: String) {
        val items = JSON.parse<dynamic>(json).items
        if (items == null || !isJsArray(items) || items.length == 0) return

        val points = mutableListOf<DashboardGradePoint>()
        for (index in 0 until items.length as Int) {
            val item = dynamicArrayItem(items, index)
            val year = item.year?.toString()?.toIntOrNull() ?: continue
            val semester = item.semester?.toString().orEmpty()
            val gpaText = item.gpa?.toString().orEmpty()
            val gpa = gpaText.replace(",", "").toDoubleOrNull() ?: continue
            val semesterOrder = when (semester) {
                "FIRST" -> 1
                "SUMMER" -> 2
                "SECOND" -> 3
                "WINTER" -> 4
                else -> 0
            }
            points += DashboardGradePoint(
                sortKey = year * 10 + semesterOrder,
                label = "${year.toString().takeLast(2)}-${semesterShortLabel(semester)}",
                gpaText = gpaText,
                gpa = gpa,
            )
        }
        val recent = points.sortedBy { it.sortKey }.takeLast(5)
        if (recent.isEmpty()) return

        element<HTMLElement>("grade-hero-value").textContent = recent.last().gpaText
        element<HTMLElement>("grade-trend-chart").apply {
            setAttribute("aria-label", "최근 ${recent.size}개 학기의 성적 추이")
            innerHTML = recent.mapIndexed { index, point ->
                val percentage = ((point.gpa / 4.5) * 100).toInt().coerceIn(8, 100)
                val currentClass = if (index == recent.lastIndex) " class=\"current\"" else ""
                "<div$currentClass><b>${point.gpaText.escapeHtml()}</b><span style=\"--bar: $percentage%\"></span><small>${point.label.escapeHtml()}</small></div>"
            }.joinToString("")
        }
    }

    private fun updateTimetableSummary(json: String) {
        val parsed = JSON.parse<dynamic>(json)
        val items = parsed.items
        if (items == null || !isJsArray(items) || items.length == 0) return

        val period = listOf(
            parsed.year?.toString().orEmpty(),
            semesterLabel(parsed.semester?.toString().orEmpty()),
        ).filter { it.isNotBlank() }.joinToString(" ")
        element<HTMLElement>("timetable-period-label").textContent = period.ifBlank { "이번 학기" }
        val preview = element<HTMLElement>("schedule-preview")
        preview.classList.add("is-loaded")
        preview.innerHTML = dashboardTimetableHtml(json)
    }

    private fun updateChapelSummary(json: String) {
        val parsed = JSON.parse<dynamic>(json)
        val seats = parsed.seatStatusTable?.items
        val attendance = parsed.attendanceTable?.items
        val seatCount = if (seats != null && isJsArray(seats)) seats.length as Int else 0
        val attendanceCount = if (attendance != null && isJsArray(attendance)) attendance.length as Int else 0
        var absenceCount = 0
        if (attendanceCount > 0) {
            for (index in 0 until attendanceCount) {
                val status = dynamicArrayItem(attendance, index).status?.toString().orEmpty()
                if (listOf("결석", "미출석").any { it in status }) absenceCount += 1
            }
        }
        val seat = if (seatCount > 0) dynamicArrayItem(seats, 0).seatNo?.toString().orEmpty() else ""
        element<HTMLElement>("chapel-summary-title").textContent = if (seat.isBlank()) "출결 ${attendanceCount}회" else "${seat}번 좌석"
        element<HTMLElement>("chapel-summary-detail").textContent = if (absenceCount > 0) {
            "출결 ${attendanceCount}회 중 결석 ${absenceCount}회가 확인됐어요."
        } else {
            "현재 학기 출결 ${attendanceCount}회를 확인했어요."
        }
    }

    private fun setDeveloperLoading(value: Boolean) {
        val button = element<HTMLButtonElement>("dev-run-button")
        button.disabled = value
        document.querySelectorAll("[data-dev-endpoint]").forEachElement { endpointButton ->
            (endpointButton as? HTMLButtonElement)?.disabled = value
        }
        if (value) {
            button.innerHTML = "<span class=\"spinner-border spinner-border-sm\" aria-hidden=\"true\"></span><span>요청 중</span>"
            element<HTMLElement>("dev-response-empty").classList.add("d-none")
            element<HTMLElement>("dev-response-panel").classList.add("d-none")
            element<HTMLElement>("dev-response-loading").classList.remove("d-none")
        } else {
            updateDeveloperRunButton()
            element<HTMLElement>("dev-response-loading").classList.add("d-none")
        }
    }

    private fun renderDeveloperResponse(content: String, success: Boolean, statusText: String? = null) {
        val status = element<HTMLElement>("dev-response-status")
        status.classList.toggle("error", !success)
        element<HTMLElement>("dev-response-status-text").textContent = statusText ?: if (success) "200 OK" else "REQUEST FAILED"
        element<HTMLElement>("dev-response-json").textContent = content
        element<HTMLButtonElement>("dev-copy-button").classList.toggle("d-none", !success)
        element<HTMLElement>("dev-response-empty").classList.add("d-none")
        element<HTMLElement>("dev-response-loading").classList.add("d-none")
        element<HTMLElement>("dev-response-panel").classList.remove("d-none")
    }

    private fun resolveDeveloperJson(
        endpoint: ApiEndpoint,
        year: String,
        semester: Semester,
        freshJson: String,
    ): Pair<String, Boolean> {
        val cacheKey = developerCacheKey(endpoint, year, semester)
        if (!isEmptyApiJson(endpoint, freshJson)) {
            developerResponseCache[cacheKey] = freshJson
            return freshJson to false
        }

        val cachedJson = developerResponseCache[cacheKey]
            ?: dashboardResponses[endpoint]?.json?.takeIf {
                matchesDeveloperParameters(endpoint, year, semester, it)
            }
        return if (cachedJson != null && !isEmptyApiJson(endpoint, cachedJson)) {
            developerResponseCache[cacheKey] = cachedJson
            cachedJson to true
        } else {
            freshJson to false
        }
    }

    private fun developerCacheKey(endpoint: ApiEndpoint, year: String, semester: Semester): String = buildString {
        append(endpoint.name)
        if (endpoint.hasSemesterParameters) append(":${year.trim()}:${semester.name}")
    }

    private fun matchesDeveloperParameters(
        endpoint: ApiEndpoint,
        year: String,
        semester: Semester,
        json: String,
    ): Boolean {
        if (!endpoint.hasSemesterParameters) return true
        return try {
            val parsed = JSON.parse<dynamic>(json)
            parsed.year?.toString() == year.trim() && parsed.semester?.toString() == semester.name
        } catch (_: Throwable) {
            false
        }
    }

    private fun clearDeveloperResponse() {
        lastDeveloperJson = ""
        element<HTMLElement>("dev-response-panel").classList.add("d-none")
        element<HTMLElement>("dev-response-loading").classList.add("d-none")
        element<HTMLElement>("dev-response-empty").classList.remove("d-none")
        element<HTMLButtonElement>("dev-copy-button").classList.add("d-none")
        element<HTMLElement>("dev-response-json").textContent = ""
    }

    private fun renderPreview(endpoint: ApiEndpoint, json: String) {
        val preview = element<HTMLElement>("preview-response")
        preview.innerHTML = when (endpoint) {
            ApiEndpoint.TIMETABLE -> timetableHtml(json)
            ApiEndpoint.GRADES -> gradeTableHtml(json)
            ApiEndpoint.SEMESTER_GRADE_SUMMARY -> semesterSummaryHtml(json)
            ApiEndpoint.SCHOLARSHIP -> scholarshipHtml(json)
            ApiEndpoint.TUITION -> tuitionHtml(json)
            ApiEndpoint.GRADUATE -> graduateTableHtml(json)
            ApiEndpoint.CHAPEL -> chapelHtml(json)
            ApiEndpoint.TERMS -> termsHtml(json)
        }
        bindRecordNavigation(preview)
    }

    private fun bindRecordNavigation(preview: HTMLElement) {
        preview.querySelectorAll("[data-record-endpoint]").forEachElement { control ->
            control.addEventListener("click", {
                val name = control.getAttribute("data-record-endpoint") ?: return@addEventListener
                selectEndpoint(ApiEndpoint.valueOf(name))
            })
        }
    }

    private fun renderApiError(message: String) {
        setResponseStatus(success = false)
        element<HTMLElement>("preview-response").innerHTML = """
            <div class="alert alert-danger d-flex gap-3 align-items-start mb-0" role="alert">
                <i class="bi bi-exclamation-octagon-fill"></i>
                <div><strong>요청을 완료하지 못했습니다.</strong><div class="mt-1">${message.escapeHtml()}</div></div>
            </div>
        """.trimIndent()
    }

    private fun setResponseStatus(success: Boolean) {
        val status = element<HTMLElement>("response-status")
        status.classList.toggle("error", !success)
        element<HTMLElement>("response-status-text").textContent = if (success) "불러오기 완료" else "불러오기 실패"
    }

    private fun showResponsePanel() {
        element<HTMLElement>("response-empty").classList.add("d-none")
        element<HTMLElement>("response-loading").classList.add("d-none")
        element<HTMLElement>("response-panel").classList.remove("d-none")
    }

    private fun clearResponse() {
        element<HTMLElement>("response-panel").classList.add("d-none")
        element<HTMLElement>("response-loading").classList.add("d-none")
        element<HTMLElement>("response-empty").classList.remove("d-none")
        element<HTMLElement>("preview-response").textContent = ""
    }

    private fun showLoginError(message: String?) {
        val alert = element<HTMLElement>("login-error")
        alert.classList.toggle("d-none", message == null)
        alert.textContent = message.orEmpty()
    }

    private fun showLoginModal() {
        window.asDynamic().bootstrap.Modal.getOrCreateInstance(element<HTMLElement>("login-modal")).show()
        window.setTimeout({ element<HTMLInputElement>("student-id").focus() }, 250)
    }

    private fun hideLoginModal() {
        window.asDynamic().bootstrap.Modal.getOrCreateInstance(element<HTMLElement>("login-modal")).hide()
    }
}

private suspend fun executeApi(
    endpoint: ApiEndpoint,
    year: String,
    semester: Semester,
    timeoutMs: Long = DASHBOARD_REQUEST_TIMEOUT_MS,
    onSuccess: (String) -> Unit,
    onFailure: (String) -> Unit,
) {
    try {
        withTimeout(timeoutMs) {
            when (endpoint) {
                ApiEndpoint.TIMETABLE -> {
                    val result = suspendCancellableCoroutine<LmsTimetableResult> { continuation ->
                        LmsApi.getTimetable(year, semester) { continuation.resume(it) }
                    }
                    result.timetable.takeIf { result.success && it != null }
                        ?.let { onSuccess(prettyJson.encodeToString(it)) }
                        ?: onFailure(result.errorMessage ?: "시간표 조회에 실패했습니다.")
                }

                ApiEndpoint.GRADES -> {
                    val result = suspendCancellableCoroutine<LmsGradeResult> { continuation ->
                        LmsApi.getGradeTable(year, semester) { continuation.resume(it) }
                    }
                    result.gradeTable.takeIf { result.success && it != null }
                        ?.let { onSuccess(prettyJson.encodeToString(it)) }
                        ?: onFailure(result.errorMessage ?: "성적표 조회에 실패했습니다.")
                }

                ApiEndpoint.SEMESTER_GRADE_SUMMARY -> {
                    val result = suspendCancellableCoroutine<LmsSemesterGradeSummaryResult> { continuation ->
                        LmsApi.getSemesterGradeSummaryTable { continuation.resume(it) }
                    }
                    result.summaryTable.takeIf { result.success && it != null }
                        ?.let { onSuccess(prettyJson.encodeToString(it)) }
                        ?: onFailure(result.errorMessage ?: "학기별 성적 요약 조회에 실패했습니다.")
                }

                ApiEndpoint.SCHOLARSHIP -> {
                    val result = suspendCancellableCoroutine<LmsScholarshipHistoryResult> { continuation ->
                        LmsApi.getScholarshipHistoryTable { continuation.resume(it) }
                    }
                    result.scholarshipHistoryTable.takeIf { result.success && it != null }
                        ?.let { onSuccess(prettyJson.encodeToString(it)) }
                        ?: onFailure(result.errorMessage ?: "장학금 이력 조회에 실패했습니다.")
                }

                ApiEndpoint.TUITION -> {
                    val result = suspendCancellableCoroutine<LmsTuitionResult> { continuation ->
                        LmsApi.getTuitionTable { continuation.resume(it) }
                    }
                    result.tuitionTable.takeIf { result.success && it != null }
                        ?.let { onSuccess(prettyJson.encodeToString(it)) }
                        ?: onFailure(result.errorMessage ?: "등록금 조회에 실패했습니다.")
                }

                ApiEndpoint.GRADUATE -> {
                    val result = suspendCancellableCoroutine<LmsGraduateTableResult> { continuation ->
                        LmsApi.getGraduateTable { continuation.resume(it) }
                    }
                    result.graduateTable.takeIf { result.success && it != null }
                        ?.let { onSuccess(prettyJson.encodeToString(it)) }
                        ?: onFailure(result.errorMessage ?: "졸업 사정표 조회에 실패했습니다.")
                }

                ApiEndpoint.CHAPEL -> {
                    val result = suspendCancellableCoroutine<LmsChapelResult> { continuation ->
                        LmsApi.getChapelTable(year, semester) { continuation.resume(it) }
                    }
                    result.chapelInformation.takeIf { result.success && it != null }
                        ?.let { onSuccess(prettyJson.encodeToString(it)) }
                        ?: onFailure(result.errorMessage ?: "채플 정보 조회에 실패했습니다.")
                }

                ApiEndpoint.TERMS -> {
                    val result = suspendCancellableCoroutine<LmsTermsResult> { continuation ->
                        LmsApi.getTerms { continuation.resume(it) }
                    }
                    if (result.success) {
                        onSuccess(prettyJson.encodeToString(result.terms))
                    } else {
                        onFailure(result.errorMessage ?: "LMS 학기 목록 조회에 실패했습니다.")
                    }
                }
            }
        }
    } catch (error: Throwable) {
        val message = if (error is kotlinx.coroutines.TimeoutCancellationException) {
            "요청이 ${timeoutMs / 1000}초 안에 완료되지 않았습니다. 잠시 후 다시 시도해주세요."
        } else {
            error.message ?: "데이터를 조회하는 중 알 수 없는 오류가 발생했습니다."
        }
        onFailure(message)
    }
}

private fun isEmptyApiJson(endpoint: ApiEndpoint, json: String): Boolean = try {
    val parsed = JSON.parse<dynamic>(json)
    when (endpoint) {
        ApiEndpoint.TERMS -> isJsArray(parsed) && parsed.length == 0
        ApiEndpoint.CHAPEL -> listOf("seatStatusTable", "attendanceTable", "absenceTable").all { tableName ->
            val items = parsed[tableName]?.items
            items == null || (isJsArray(items) && items.length == 0)
        }
        else -> {
            val items = parsed.items
            items == null || (isJsArray(items) && items.length == 0)
        }
    }
} catch (_: Throwable) {
    false
}

private val ApiEndpoint.supportsSuspend: Boolean
    get() = this != ApiEndpoint.TERMS

private val ApiEndpoint.resultProperty: String
    get() = when (this) {
        ApiEndpoint.TIMETABLE -> "timetable"
        ApiEndpoint.GRADES -> "gradeTable"
        ApiEndpoint.SEMESTER_GRADE_SUMMARY -> "summaryTable"
        ApiEndpoint.SCHOLARSHIP -> "scholarshipHistoryTable"
        ApiEndpoint.TUITION -> "tuitionTable"
        ApiEndpoint.GRADUATE -> "graduateTable"
        ApiEndpoint.CHAPEL -> "chapelInformation"
        ApiEndpoint.TERMS -> "terms"
    }

private fun ApiEndpoint.usageSnippet(
    language: String,
    usage: String,
    year: String,
    semester: String,
): String {
    val method = functionName.substringAfter("LmsApi.").removeSuffix("()")
    val kotlinSemester = "Semester.$semester"
    val swiftSemester = ".${semester.lowercase()}"
    val kotlinArguments = if (hasSemesterParameters) "year = \"$year\", semester = $kotlinSemester" else ""
    val swiftArguments = if (hasSemesterParameters) "year: \"$year\", semester: $swiftSemester" else ""

    if (usage == "suspend") {
        return if (language == "swift") {
            "let response = try await lmsApi.$method($swiftArguments)"
        } else {
            "val response = LmsApi.$method($kotlinArguments)"
        }
    }

    return if (language == "swift") {
        val prefix = if (swiftArguments.isBlank()) "" else "$swiftArguments, "
        """
        lmsApi.$method(${prefix}completion: { result in
            guard result.success else {
                print(result.errorMessage ?? "Request failed")
                return
            }
            let data = result.$resultProperty
            print(data as Any)
        })
        """.trimIndent()
    } else {
        val prefix = if (kotlinArguments.isBlank()) "" else "$kotlinArguments, "
        """
        LmsApi.$method(${prefix}completion = { result ->
            if (result.success) {
                val data = result.$resultProperty
                println(data)
            } else {
                println(result.errorMessage)
            }
        })
        """.trimIndent()
    }
}

private fun String.toSemester(): Semester = when (this) {
    "SECOND" -> Semester.SECOND
    "SUMMER" -> Semester.SUMMER
    "WINTER" -> Semester.WINTER
    else -> Semester.FIRST
}

private fun gradeTableHtml(json: String): String {
    val parsed = JSON.parse<dynamic>(json)
    val items = parsed.items
    if (items == null || !isJsArray(items) || items.length == 0) {
        return emptyPreviewHtml("표시할 과목별 성적이 없습니다.")
    }

    val courses = buildString {
        for (index in 0 until items.length as Int) {
            val item = items[index]
            val grade = item.grade?.toString().orEmpty()
            append(
                """
                <article class="grade-course">
                    <div class="grade-course-main">
                        <span class="grade-course-code">${stringValue(item.subjectCode)}</span>
                        <h3>${stringValue(item.subjectName)}</h3>
                        <p>${stringValue(item.classification)} · ${stringValue(item.credits)}학점 · ${stringValue(item.professor)}</p>
                    </div>
                    <div class="grade-course-result ${gradeToneClass(grade)}">
                        <strong>${grade.ifBlank { "-" }.escapeHtml()}</strong>
                        <span>평점 ${stringValue(item.gradePoint)}</span>
                    </div>
                </article>
                """.trimIndent(),
            )
        }
    }
    val semester = listOf(parsed.year?.toString().orEmpty(), semesterLabel(parsed.semester?.toString().orEmpty()))
        .filter { it.isNotBlank() }
        .joinToString(" ")
    return """
        <div class="response-summary mb-3">
            <div><span class="summary-label">$semester</span><strong>과목별 성적</strong></div>
            <div class="text-body-secondary small">${items.length}개 과목</div>
        </div>
        <div class="grade-course-list">$courses</div>
    """.trimIndent()
}

private fun gradeToneClass(grade: String): String = when {
    grade.startsWith("A", ignoreCase = true) -> "is-excellent"
    grade.startsWith("B", ignoreCase = true) -> "is-good"
    grade.equals("F", ignoreCase = true) || grade.contains("NP", ignoreCase = true) -> "is-failed"
    else -> "is-neutral"
}

private fun semesterSummaryHtml(json: String): String {
    val parsed = JSON.parse<dynamic>(json)
    val items = parsed.items
    if (items == null || !isJsArray(items) || items.length == 0) {
        return emptyPreviewHtml("표시할 학기별 성적 요약이 없습니다.")
    }

    val summaries = buildString {
        for (index in 0 until items.length as Int) {
            val item = items[index]
            val semester = semesterLabel(item.semester?.toString().orEmpty()).ifBlank { "기타" }
            append(
                """
                <article class="semester-result">
                    <div class="semester-result-title">
                        <div><strong>${stringValue(item.year)}</strong><span>$semester</span></div>
                        <div class="semester-gpa"><small>평점</small><strong>${stringValue(item.gpa)}</strong></div>
                    </div>
                    <div class="semester-metrics">
                        ${metricHtml("신청학점", item.attemptedCredits)}
                        ${metricHtml("취득학점", item.earnedCredits)}
                        ${metricHtml("P/F 학점", item.pfCredits)}
                        ${metricHtml("산술평균", item.arithmeticMean)}
                        ${metricHtml("학기 석차", item.semesterRank)}
                        ${metricHtml("전체 석차", item.totalRank)}
                    </div>
                    <div class="semester-flags">
                        <span>학사경고 ${statusBadge(item.academicWarning)}</span>
                        <span>상담 ${statusBadge(item.consultationStatus)}</span>
                        <span>유급 ${statusBadge(item.failedYearStatus)}</span>
                    </div>
                </article>
                """.trimIndent(),
            )
        }
    }

    return """
        <div class="response-summary mb-3">
            <div>
                <span class="summary-label">조회 결과</span>
                <strong>${items.length}개 학기</strong>
            </div>
            <div class="text-body-secondary small">학점·평점·석차 및 학사 상태</div>
        </div>
        <div class="semester-result-list">$summaries</div>
    """.trimIndent()
}

private fun metricHtml(label: String, value: dynamic): String = """
    <div class="semester-metric"><span>$label</span><strong>${stringValue(value)}</strong></div>
""".trimIndent()

private fun scholarshipHtml(json: String): String {
    val parsed = JSON.parse<dynamic>(json)
    val items = parsed.items
    if (items == null || !isJsArray(items) || items.length == 0) {
        return emptyPreviewHtml("표시할 장학금 수혜 이력이 없습니다.")
    }

    val history = buildString {
        for (index in 0 until items.length as Int) {
            val item = items[index]
            val status = item.processStatus?.toString().orEmpty()
            val semester = semesterLabel(item.semester?.toString().orEmpty())
            val period = listOf(stringValue(item.year), semester).filter { it.isNotBlank() && it != "-" }.joinToString(" ")
            val detail = item.dropReason?.toString().orEmpty()
                .ifBlank { item.note?.toString().orEmpty() }
                .ifBlank { item.paymentMethod?.toString().orEmpty() }
            val metadata = listOf(period, item.processDate?.toString().orEmpty(), detail)
                .filter { it.isNotBlank() }
                .joinToString(" · ")
            append(
                """
                <article class="record-entry scholarship-record">
                    <div class="record-entry-top">
                        <h3>${stringValue(item.scholarshipName)}</h3>${processBadge(status)}
                    </div>
                    <strong class="record-amount">${stringValue(item.actualAmount)}</strong>
                    <p class="record-meta">${metadata.escapeHtml().ifBlank { "처리 정보가 없습니다." }}</p>
                </article>
                """.trimIndent(),
            )
        }
    }
    return """
        <section class="financial-records">
            ${financialTabs(ApiEndpoint.SCHOLARSHIP)}
            <div class="record-list">$history</div>
        </section>
    """.trimIndent()
}

private fun processToneClass(status: String): String {
    val normalized = status.lowercase()
    return when {
        listOf("완료", "선발", "지급", "승인").any { it in normalized } -> "is-complete"
        listOf("탈락", "취소", "거절", "반려").any { it in normalized } -> "is-rejected"
        else -> "is-pending"
    }
}

private fun processBadge(status: String): String {
    val text = status.ifBlank { "처리 중" }.escapeHtml()
    return "<span class=\"record-status ${processToneClass(status)}\">$text</span>"
}

private fun tuitionHtml(json: String): String {
    val parsed = JSON.parse<dynamic>(json)
    val items = parsed.items
    if (items == null || !isJsArray(items) || items.length == 0) {
        return emptyPreviewHtml("표시할 등록금 납부 이력이 없습니다.")
    }

    val payments = buildString {
        for (index in 0 until items.length as Int) {
            val item = items[index]
            val semester = semesterLabel(item.semester?.toString().orEmpty())
            val paidAmount = if (item.paymentAmount?.toString().isNullOrBlank()) item.amount else item.paymentAmount
            val metadata = listOf(
                "등록일자 ${stringValue(item.registrationDate)}",
                "사전감면 ${stringValue(item.reduction)}",
            ).joinToString(" · ")
            append(
                """
                <article class="record-entry tuition-record">
                    <div class="record-entry-top">
                        <h3>${stringValue(item.year)}학년도 $semester</h3>
                        <span class="record-status is-neutral">${stringValue(item.registrationType)}</span>
                    </div>
                    <strong class="record-amount">${stringValue(paidAmount)}</strong>
                    <p class="record-meta">$metadata</p>
                </article>
                """.trimIndent(),
            )
        }
    }
    return """
        <section class="financial-records">
            ${financialTabs(ApiEndpoint.TUITION)}
            <div class="record-list">$payments</div>
        </section>
    """.trimIndent()
}

private fun financialTabs(activeEndpoint: ApiEndpoint): String = """
    <div class="record-tabs" role="tablist" aria-label="등록금 및 장학금 내역">
        <button class="record-tab ${if (activeEndpoint == ApiEndpoint.TUITION) "active" else ""}" type="button" data-record-endpoint="TUITION" aria-selected="${activeEndpoint == ApiEndpoint.TUITION}">등록금 내역</button>
        <button class="record-tab ${if (activeEndpoint == ApiEndpoint.SCHOLARSHIP) "active" else ""}" type="button" data-record-endpoint="SCHOLARSHIP" aria-selected="${activeEndpoint == ApiEndpoint.SCHOLARSHIP}">장학금 내역</button>
    </div>
""".trimIndent()

private fun termsHtml(json: String): String {
    val items = JSON.parse<dynamic>(json)
    if (!isJsArray(items) || items.length == 0) {
        return emptyPreviewHtml("표시할 LMS 학기가 없습니다.")
    }

    val terms = buildString {
        for (index in 0 until items.length as Int) {
            val item = items[index]
            append(
                """
                <article class="term-entry">
                    <div class="term-id">${stringValue(item.id)}</div>
                    <div class="term-copy"><strong>${stringValue(item.name)}</strong><span>${formatTermDate(item.start_at)} — ${formatTermDate(item.end_at)}</span></div>
                    <i class="bi bi-calendar-range"></i>
                </article>
                """.trimIndent(),
            )
        }
    }
    return """
        <div class="response-summary mb-3">
            <div><span class="summary-label">LMS 학기</span><strong>${items.length}개</strong></div>
            <div class="text-body-secondary small">Canvas enrollment terms</div>
        </div>
        <div class="term-list">$terms</div>
    """.trimIndent()
}

private fun formatTermDate(value: dynamic): String {
    val text = value?.toString().orEmpty()
    return if (text.length >= 10) text.take(10).escapeHtml() else text.ifBlank { "미정" }.escapeHtml()
}

private fun graduateTableHtml(json: String): String {
    val parsed = JSON.parse<dynamic>(json)
    val items = parsed.items
    if (items == null || !isJsArray(items) || items.length == 0) {
        return emptyPreviewHtml("표시할 졸업 사정 결과가 없습니다.")
    }

    var satisfiedCount = 0
    var insufficientCount = 0
    val classifications = mutableListOf<String>()
    for (index in 0 until items.length as Int) {
        val classification = items[index].classification?.toString().orEmpty().ifBlank { "기타" }
        if (classification !in classifications) classifications += classification
    }
    for (index in 0 until items.length as Int) {
        val item = items[index]
        when (graduateResultKind(item.result?.toString().orEmpty())) {
            GraduateResultKind.SATISFIED -> satisfiedCount += 1
            GraduateResultKind.INSUFFICIENT -> insufficientCount += 1
            GraduateResultKind.NEUTRAL -> Unit
        }
    }
    val groups = classifications.joinToString("") { classification ->
        val requirements = buildString {
            for (index in 0 until items.length as Int) {
                val item = items[index]
                val itemClassification = item.classification?.toString().orEmpty().ifBlank { "기타" }
                if (itemClassification != classification) continue
                val stateClass = when (graduateResultKind(item.result?.toString().orEmpty())) {
                    GraduateResultKind.SATISFIED -> "is-satisfied"
                    GraduateResultKind.INSUFFICIENT -> "is-insufficient"
                    GraduateResultKind.NEUTRAL -> "is-neutral"
                }
                append(
                    """
                    <div class="graduate-requirement $stateClass">
                        <div class="graduate-requirement-heading">
                            <strong>${stringValue(item.requirement)}</strong>
                            ${graduateResultBadge(item.result)}
                        </div>
                        <span class="graduate-requirement-meta">기준 ${stringValue(item.standardValue)} · 계산 ${stringValue(item.calculatedValue)}${graduateDifferenceHtml(item.difference)}</span>
                    </div>
                    """.trimIndent(),
                )
            }
        }
        """
        <section class="graduate-group">
            <h3>${classification.escapeHtml()}</h3>
            <div>$requirements</div>
        </section>
        """.trimIndent()
    }

    return """
        <div class="graduate-overview ${if (insufficientCount > 0) "is-insufficient" else "is-satisfied"}">
            <span>졸업사정결과 · <strong>${if (insufficientCount > 0) "불가능" else "가능"}</strong></span>
            <button type="button" data-record-endpoint="GRADES">과목상세 보기</button>
        </div>
        <div class="graduate-group-list">$groups</div>
    """.trimIndent()
}

private enum class GraduateResultKind {
    SATISFIED,
    INSUFFICIENT,
    NEUTRAL,
}

private fun graduateResultKind(value: String): GraduateResultKind {
    val normalized = value.trim().lowercase()
    return when {
        normalized.isBlank() -> GraduateResultKind.NEUTRAL
        listOf("미충족", "부족", "불합격", "미달", "미이수").any { it in normalized } -> GraduateResultKind.INSUFFICIENT
        normalized in setOf("n", "no", "false") -> GraduateResultKind.INSUFFICIENT
        listOf("충족", "합격", "완료", "이수", "가능").any { it in normalized } -> GraduateResultKind.SATISFIED
        normalized in setOf("y", "yes", "true") -> GraduateResultKind.SATISFIED
        else -> GraduateResultKind.NEUTRAL
    }
}

private fun graduateResultBadge(value: dynamic): String {
    val raw = value?.toString().orEmpty()
    val text = raw.ifBlank { "-" }.escapeHtml()
    val style = when (graduateResultKind(raw)) {
        GraduateResultKind.SATISFIED -> "is-satisfied"
        GraduateResultKind.INSUFFICIENT -> "is-insufficient"
        GraduateResultKind.NEUTRAL -> "is-neutral"
    }
    return "<span class=\"record-status $style\">$text</span>"
}

private fun graduateDifferenceHtml(value: dynamic): String {
    val raw = value?.toString().orEmpty()
    if (raw.isBlank()) return ""
    val number = raw.replace(",", "").toDoubleOrNull()
    val style = when {
        number == null -> "is-neutral"
        number < 0 -> "is-insufficient"
        else -> "is-satisfied"
    }
    return " · <b class=\"graduate-difference $style\">${raw.escapeHtml()}</b>"
}

private fun chapelHtml(json: String): String {
    val parsed = JSON.parse<dynamic>(json)
    val seats = parsed.seatStatusTable.items
    val attendance = parsed.attendanceTable.items
    val seatCount = if (seats != null && isJsArray(seats)) seats.length as Int else 0
    val attendanceCount = if (attendance != null && isJsArray(attendance)) attendance.length as Int else 0
    if (seatCount == 0 && attendanceCount == 0) {
        return emptyPreviewHtml("표시할 채플 좌석 및 출결 정보가 없습니다.")
    }

    val semester = listOf(parsed.year?.toString().orEmpty(), semesterLabel(parsed.semester?.toString().orEmpty()))
        .filter { it.isNotBlank() }
        .joinToString(" ")
    val seatHtml = if (seatCount == 0) {
        "<p class=\"chapel-empty mb-0\">배정된 좌석 정보가 없습니다.</p>"
    } else {
        buildString {
            for (index in 0 until seatCount) {
                val seat = dynamicArrayItem(seats, index)
                append(
                    """
                    <div class="chapel-seat-row">
                        ${chapelSeatValue("분반", seat.classGroup, "bi-people")}
                        ${chapelSeatValue("시간", seat.timetable, "bi-clock")}
                        ${chapelSeatValue("강의실", seat.classroom, "bi-geo-alt")}
                        ${chapelSeatValue("좌석", seat.seatNo, "bi-person-workspace", emphasized = true)}
                        ${chapelSeatValue("결석", seat.absenceCount, "bi-calendar-x")}
                        ${chapelSeatValue("성적", seat.gradeResult, "bi-check2-circle")}
                    </div>
                    """.trimIndent(),
                )
            }
        }
    }
    val attendanceHtml = if (attendanceCount == 0) {
        "<p class=\"chapel-empty mb-0\">등록된 일자별 출결 정보가 없습니다.</p>"
    } else {
        buildString {
            for (index in 0 until attendanceCount) {
                val item = dynamicArrayItem(attendance, index)
                val status = item.status?.toString().orEmpty()
                val statusClass = chapelAttendanceClass(status)
                append(
                    """
                    <article class="chapel-attendance-item $statusClass">
                        <div class="chapel-attendance-marker"><span></span></div>
                        <time>${stringValue(item.date)}</time>
                        <strong>${status.ifBlank { "미확인" }.escapeHtml()}</strong>
                        <small>${stringValue(item.lectureType)} · ${stringValue(item.classGroup)}분반</small>
                    </article>
                    """.trimIndent(),
                )
            }
        }
    }

    return """
        <div class="response-summary mb-3">
            <div><span class="summary-label">$semester</span><strong>채플 출결</strong></div>
            <div class="text-body-secondary small">총 ${attendanceCount}회</div>
        </div>
        <section class="chapel-section" aria-labelledby="chapel-seat-heading">
            <div class="chapel-section-heading">
                <div><i class="bi bi-person-badge"></i><h3 id="chapel-seat-heading">좌석 정보</h3></div>
                <span>${seatCount}건</span>
            </div>
            <div class="chapel-seat-list">$seatHtml</div>
        </section>
        <section class="chapel-section" aria-labelledby="chapel-attendance-heading">
            <div class="chapel-section-heading">
                <div><i class="bi bi-calendar-check"></i><h3 id="chapel-attendance-heading">일자별 출석</h3></div>
                <span>${attendanceCount}회</span>
            </div>
            <div class="chapel-attendance-scroll"><div class="chapel-attendance-timeline">$attendanceHtml</div></div>
        </section>
    """.trimIndent()
}

private fun chapelSeatValue(label: String, value: dynamic, icon: String, emphasized: Boolean = false): String {
    val emphasisClass = if (emphasized) " is-emphasized" else ""
    return """
        <div class="chapel-seat-value$emphasisClass">
            <span><i class="bi $icon"></i>$label</span>
            <strong>${stringValue(value)}</strong>
        </div>
    """.trimIndent()
}

private fun chapelAttendanceClass(status: String): String {
    val normalized = status.trim().lowercase()
    return when {
        listOf("결석", "미출석", "결강").any { it in normalized } -> "is-absent"
        listOf("지각", "조퇴").any { it in normalized } -> "is-late"
        listOf("출석", "인정", "참석").any { it in normalized } -> "is-present"
        else -> "is-unknown"
    }
}

private fun semesterLabel(value: String): String = when (value) {
    "FIRST" -> "1학기"
    "SECOND" -> "2학기"
    "SUMMER" -> "여름학기"
    "WINTER" -> "겨울학기"
    else -> value
}

private fun semesterShortLabel(value: String): String = when (value) {
    "FIRST" -> "1"
    "SECOND" -> "2"
    "SUMMER" -> "S"
    "WINTER" -> "W"
    else -> "-"
}

private fun dayShortLabel(value: String): String = when (value) {
    "MONDAY" -> "월"
    "TUESDAY" -> "화"
    "WEDNESDAY" -> "수"
    "THURSDAY" -> "목"
    "FRIDAY" -> "금"
    "SATURDAY" -> "토"
    "SUNDAY" -> "일"
    else -> "-"
}

private data class TimetablePreviewItem(
    val day: String,
    val period: String,
    val periodTime: String,
    val subject: String,
    val professor: String,
    val classroom: String,
    val time: String,
)

private fun dashboardTimetableHtml(json: String): String {
    val parsed = JSON.parse<dynamic>(json)
    val rawItems = parsed.items
    if (rawItems == null || !isJsArray(rawItems) || rawItems.length == 0) {
        return emptyPreviewHtml("표시할 시간표가 없습니다.")
    }

    val items = buildList {
        for (index in 0 until rawItems.length as Int) {
            val item = dynamicArrayItem(rawItems, index)
            add(
                TimetablePreviewItem(
                    day = item.dayOfWeek?.toString().orEmpty(),
                    period = item.period?.toString().orEmpty(),
                    periodTime = item.periodTime?.toString().orEmpty(),
                    subject = item.subject?.toString().orEmpty(),
                    professor = item.professor?.toString().orEmpty(),
                    classroom = item.classroom?.toString().orEmpty(),
                    time = item.time?.toString().orEmpty(),
                ),
            )
        }
    }
    val allDays = listOf(
        "MONDAY" to "월",
        "TUESDAY" to "화",
        "WEDNESDAY" to "수",
        "THURSDAY" to "목",
        "FRIDAY" to "금",
        "SATURDAY" to "토",
        "SUNDAY" to "일",
    )
    val scheduledDays = items.map { it.day }.toSet()
    val activeDays = allDays.filterIndexed { index, (day, _) -> index < 5 || day in scheduledDays }
    val periods = items
        .map { it.period to it.periodTime }
        .distinct()
        .sortedWith(compareBy({ it.first.periodSortKey() }, { it.first }))

    val dayHeaders = activeDays.joinToString("") { (_, label) ->
        "<div class=\"dashboard-timetable-day\">${label.escapeHtml()}</div>"
    }
    val rows = periods.joinToString("") { (period, periodTime) ->
        val timeLabel = periodTime.ifBlank { period }
        val label = """
            <div class="dashboard-timetable-time">
                <strong>${period.escapeHtml()}</strong>
                <span>${timeLabel.escapeHtml()}</span>
            </div>
        """.trimIndent()
        val cells = activeDays.joinToString("") { (day, _) ->
            val scheduled = items.filter { it.day == day && it.period == period }
            val events = scheduled.joinToString("") { item ->
                val tone = (item.subject.hashCode() and Int.MAX_VALUE) % 5
                val details = listOf(item.professor, item.classroom)
                    .filter { it.isNotBlank() }
                    .joinToString(" · ")
                """
                <article class="dashboard-course-block tone-$tone">
                    <strong>${item.subject.escapeHtml()}</strong>
                    <span>${details.escapeHtml()}</span>
                    <small>${item.time.ifBlank { item.periodTime }.escapeHtml()}</small>
                </article>
                """.trimIndent()
            }
            "<div class=\"dashboard-timetable-cell${if (scheduled.isNotEmpty()) " has-course" else ""}\">$events</div>"
        }
        label + cells
    }

    return """
        <div class="dashboard-timetable-scroll">
            <div class="dashboard-timetable-board" style="--dashboard-days: ${activeDays.size}">
                <div class="dashboard-timetable-corner">시간</div>
                $dayHeaders
                $rows
            </div>
        </div>
    """.trimIndent()
}

private fun timetableHtml(json: String): String {
    val parsed = JSON.parse<dynamic>(json)
    val rawItems = parsed.items
    if (rawItems == null || !isJsArray(rawItems) || rawItems.length == 0) {
        return emptyPreviewHtml("표시할 시간표가 없습니다.")
    }

    val items = buildList {
        for (index in 0 until rawItems.length as Int) {
            val item = rawItems[index]
            add(
                TimetablePreviewItem(
                    day = item.dayOfWeek?.toString().orEmpty(),
                    period = item.period?.toString().orEmpty(),
                    periodTime = item.periodTime?.toString().orEmpty(),
                    subject = item.subject?.toString().orEmpty(),
                    professor = item.professor?.toString().orEmpty(),
                    classroom = item.classroom?.toString().orEmpty(),
                    time = item.time?.toString().orEmpty(),
                ),
            )
        }
    }
    val dayLabels = listOf(
        "MONDAY" to "월",
        "TUESDAY" to "화",
        "WEDNESDAY" to "수",
        "THURSDAY" to "목",
        "FRIDAY" to "금",
        "SATURDAY" to "토",
        "SUNDAY" to "일",
    )
    val activeDays = dayLabels.filter { (day, _) -> day in items.map { it.day } }
        .ifEmpty { dayLabels.take(5) }
    val periods = items
        .map { it.period to it.periodTime }
        .distinct()
        .sortedWith(compareBy({ it.first.periodSortKey() }, { it.first }))

    val dayHeaders = activeDays.joinToString("") { (_, label) ->
        "<div class=\"timetable-day-header\">$label</div>"
    }
    val rows = periods.joinToString("") { (period, periodTime) ->
        val label = """
            <div class="timetable-period-label">
                <strong>${period.escapeHtml()}</strong>
                <span>${periodTime.escapeHtml()}</span>
            </div>
        """.trimIndent()
        val dayCells = activeDays.joinToString("") { (day, _) ->
            val scheduled = items.filter { it.day == day && it.period == period }
            if (scheduled.isEmpty()) {
                "<div class=\"timetable-slot\"></div>"
            } else {
                val events = scheduled.joinToString("") { item ->
                    val details = listOf(item.professor, item.classroom, item.time)
                        .filter { it.isNotBlank() }
                        .joinToString(" · ")
                    """
                    <article class="timetable-event">
                        <strong>${item.subject.escapeHtml()}</strong>
                        <span>${details.escapeHtml()}</span>
                    </article>
                    """.trimIndent()
                }
                "<div class=\"timetable-slot has-event\">$events</div>"
            }
        }
        label + dayCells
    }

    val semester = listOf(parsed.year?.toString().orEmpty(), parsed.semester?.toString().orEmpty())
        .filter { it.isNotBlank() }
        .joinToString(" ")
    return """
        <div class="response-summary mb-3">
            <div><span class="summary-label">$semester</span><strong>${items.size}개 수업</strong></div>
            <div class="text-body-secondary small">요일과 교시 기준 시간표</div>
        </div>
        <div class="timetable-wrap">
            <div class="timetable-board" style="--timetable-days: ${activeDays.size}">
                <div class="timetable-corner">교시</div>
                $dayHeaders
                $rows
            </div>
        </div>
    """.trimIndent()
}

private fun String.periodSortKey(): Int = Regex("""\d+""").find(this)?.value?.toIntOrNull() ?: Int.MAX_VALUE

private fun genericPreviewHtml(json: String): String {
    val parsed = JSON.parse<dynamic>(json)
    val collection = when {
        isJsArray(parsed) -> parsed
        parsed.items != null && isJsArray(parsed.items) -> parsed.items
        else -> null
    }

    if (collection == null) {
        val keys = objectKeys(parsed)
        if (keys.isEmpty()) return emptyPreviewHtml("표시할 데이터가 없습니다.")
        val rows = keys.joinToString("") { key ->
            "<tr><th>${key.escapeHtml()}</th><td>${displayValue(parsed[key])}</td></tr>"
        }
        return "<div class=\"table-responsive response-table-wrap\"><table class=\"table align-middle mb-0\"><tbody>$rows</tbody></table></div>"
    }

    if (collection.length == 0) return emptyPreviewHtml("표시할 데이터가 없습니다.")
    val keys = objectKeys(collection[0]).take(7)
    val head = keys.joinToString("") { "<th>${it.humanize().escapeHtml()}</th>" }
    val body = buildString {
        for (index in 0 until collection.length as Int) {
            append("<tr>")
            keys.forEach { key -> append("<td>${displayValue(collection[index][key])}</td>") }
            append("</tr>")
        }
    }
    return """
        <div class="response-summary mb-3"><div><span class="summary-label">조회 결과</span><strong>${collection.length}개 항목</strong></div></div>
        <div class="table-responsive response-table-wrap">
            <table class="table table-hover align-middle mb-0"><thead><tr>$head</tr></thead><tbody>$body</tbody></table>
        </div>
    """.trimIndent()
}

private fun emptyPreviewHtml(message: String): String = """
    <div class="empty-response compact">
        <i class="bi bi-inbox"></i>
        <p class="mb-0">${message.escapeHtml()}</p>
    </div>
""".trimIndent()

private fun displayValue(value: dynamic): String {
    if (value == null) return "<span class=\"text-body-tertiary\">null</span>"
    if (isJsArray(value)) return "<span class=\"badge text-bg-light border\">${value.length}개</span>"
    if (jsTypeOf(value) == "object") return "<code>{ ... }</code>"
    return value.toString().escapeHtml()
}

private fun statusBadge(value: dynamic): String {
    val text = value?.toString().orEmpty().ifBlank { "-" }.escapeHtml()
    val positive = text in setOf("N", "없음", "미대상", "아니오", "-")
    val style = if (positive) "text-bg-light border" else "text-bg-warning"
    return "<span class=\"badge $style\">$text</span>"
}

private fun stringValue(value: dynamic): String = value?.toString().orEmpty().ifBlank { "-" }.escapeHtml()

private fun currentDiagnosticTimestamp(): String = js("new Date().toISOString()").toString()

private fun sanitizeDiagnosticMessage(message: String): String {
    val secretAssignment = Regex(
        """(?i)\b(password|passwd|token|authorization|cookie|student[_-]?id|unique_id|sToken)\b\s*[:=]\s*[^\s,;]+""",
    )
    return secretAssignment
        .replace(message.take(1500)) { match -> "${match.groupValues[1]}=[REDACTED]" }
        .replace(Regex("""(?i)Bearer\s+[A-Za-z0-9._~+/=-]+"""), "Bearer [REDACTED]")
        .replace(Regex("""https?://[^\s?]+\?[^\s]+""")) { match -> "${match.value.substringBefore("?")}?[REDACTED_QUERY]" }
        .replace(Regex("""\b\d{8,10}\b"""), "[REDACTED_ID]")
}

private fun String.humanize(): String = replace('_', ' ').replace(Regex("([a-z])([A-Z])"), "$1 $2")

private fun String.escapeHtml(): String = replace("&", "&amp;")
    .replace("<", "&lt;")
    .replace(">", "&gt;")
    .replace("\"", "&quot;")
    .replace("'", "&#39;")

private fun isJsArray(value: dynamic): Boolean = js("Array.isArray(value)")

private fun dynamicArrayItem(value: dynamic, index: Int): dynamic = js("value[index]")

private fun objectKeys(value: dynamic): Array<String> = js("Object.keys(value)")

private inline fun <reified T : Element> element(id: String): T =
    document.getElementById(id) as? T ?: error("Required element #$id was not found")

private inline fun org.w3c.dom.NodeList.forEachElement(block: (HTMLElement) -> Unit) {
    for (index in 0 until length) {
        (item(index) as? HTMLElement)?.let(block)
    }
}
